/* istanbul ignore file */
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import {
  BlockType,
  DisplayAction,
  DisplayCondition,
  DisplayLogic,
  ValueOperator,
} from '@common/entities';
import { postalAddressesCountryDefinitions } from '@entrata/utils';
import { RootState } from 'state/store';

import { useInstallmentPlansQuery } from 'hooks/useInstallmentPlansQuery';
import { isDateOlderThanYears, isDateYoungerThanYears } from 'utils/dateComparison';

import { ConditionalElementState } from './conditionalElementState';
import { hasInstallmentPlanCountLogic } from './hasInstallmentPlanCountLogic';

// Type for field values that can be null/undefined when field is hidden
type MaybeHiddenField = string | null | undefined;
const isCitizenshipField = (fieldName: string) => fieldName.toLowerCase().includes('citizenship');

const normalizeCitizenshipValue = (value: string | null | undefined) => {
  if (!value) return value;
  const normalizedValue = value.toLowerCase();
  const country = Object.entries(postalAddressesCountryDefinitions).find(
    ([, definition]) => definition.name.toLowerCase() === normalizedValue,
  );
  return country?.[0] ?? value;
};

export function useEvaluateDisplayCondition() {
  const formContext = useFormContext();

  const { blockFieldMap } = useSelector((state: RootState) => state.blockFieldMap);
  const form = useSelector((state: RootState) => state.application?.form);
  const installmentPlansQuery = useInstallmentPlansQuery({
    enabled: hasInstallmentPlanCountLogic(form),
  });

  if (!formContext) return () => ConditionalElementState.Visible;

  const { watch } = formContext;

  function evaluateDisplayCondition(displayLogic?: DisplayLogic): ConditionalElementState {
    if (!displayLogic) return ConditionalElementState.Visible;
    if (!displayLogic.displayConditions.length) return ConditionalElementState.Visible;

    // A condition cannot be evaluated safely without its source field. A Show
    // condition must fail closed; Hide and Require must not hide or require the
    // target when their source cannot be resolved.
    if (
      displayLogic.displayConditions.some(
        ({ sourceBlockId, sourceBlockField, sourceField }) =>
          !sourceBlockId && !(sourceBlockField?.name ?? sourceField?.name),
      )
    ) {
      return displayLogic.displayAction === DisplayAction.Show
        ? ConditionalElementState.Hidden
        : ConditionalElementState.Visible;
    }

    const conditionMatches = displayLogic.displayConditions.map((displayCondition) => {
      if (displayCondition.sourceBlockId) {
        const selectedPlanId = watch(`${BlockType.InstallmentPlans}.selectedPlan`);
        const hasResolvedSelection =
          selectedPlanId !== undefined &&
          selectedPlanId !== null &&
          selectedPlanId !== '' &&
          selectedPlanId !== 'none';

        if (
          !hasResolvedSelection ||
          installmentPlansQuery.isError ||
          installmentPlansQuery.data?.isAllowed !== true
        ) {
          return false;
        }

        const installmentPlans = installmentPlansQuery.data?.installmentPlans;
        if (!Array.isArray(installmentPlans)) return false;

        const selectedPlan = installmentPlans.find(
          (plan) => String(plan.id) === String(selectedPlanId),
        );

        if (!selectedPlan || !Array.isArray(selectedPlan.installments)) return false;

        return evaluateFieldValues(displayCondition, [String(selectedPlan.installments.length)]);
      }

      const sourceBlockFieldName = displayCondition.sourceBlockField?.name;
      const fieldName = (sourceBlockFieldName ?? displayCondition.sourceField!.name) as string;
      const fieldValues = sourceBlockFieldName
        ? blockFieldMap
            .filter((map) => map.blockFieldName === fieldName)
            .map((formFieldMap) => watch(formFieldMap.blockFieldFormFieldName))
        : [watch(fieldName)];

      return evaluateFieldValues(displayCondition, fieldValues, isCitizenshipField(fieldName));
    });
    const valueMatch =
      displayLogic.conditionalLogicalOperator === 'or'
        ? conditionMatches.some(Boolean)
        : conditionMatches.every(Boolean);

    if (displayLogic.displayAction === DisplayAction.Require) {
      return valueMatch ? ConditionalElementState.Required : ConditionalElementState.Visible;
    }

    const shouldDisplay =
      (valueMatch && displayLogic.displayAction === DisplayAction.Show) ||
      (!valueMatch && displayLogic.displayAction === DisplayAction.Hide);

    return shouldDisplay ? ConditionalElementState.Visible : ConditionalElementState.Hidden;
  }

  function evaluateFieldValues(
    displayCondition: DisplayCondition,
    fieldValues: MaybeHiddenField[],
    shouldNormalizeCitizenship = false,
  ) {
    const caseSensitiveComparison = false;
    let match = false;
    // if there are multiple field values it is due to a repeating block such as income
    // - e.g. if they entered three "previous employers", and we are matching on employer name,
    // the match condition will be satisfied if any of the employer names match
    // Handle hidden fields (null/undefined values) by treating them as empty strings
    fieldValues.forEach((fieldValue) => {
      const normalizedFieldValue = shouldNormalizeCitizenship
        ? normalizeCitizenshipValue(fieldValue)
        : fieldValue;
      // in the case of a multi-select, return true if any values match
      // (multi select is the only scenario where there would be multiple conditional values)
      // If there's only one display condition value, of course it would just get singly evaluated here
      displayCondition.displayConditionValues.forEach((conditionValue) => {
        const { value, fieldOptionId } = conditionValue;
        const conditionValueToCompare = fieldOptionId ?? value;
        const condition = String(
          shouldNormalizeCitizenship
            ? normalizeCitizenshipValue(conditionValueToCompare)
            : (conditionValueToCompare ?? ''),
        );
        switch (conditionValue.valueOperator) {
          case ValueOperator.Equals:
            if (isEquals(normalizedFieldValue, condition, caseSensitiveComparison)) {
              match = true;
            }
            break;
          case ValueOperator.Includes:
            if (caseSensitiveComparison && normalizedFieldValue === condition) {
              match = true;
            } else if (caseInsensitiveCompare(normalizedFieldValue, condition)) {
              match = true;
            }
            break;
          case ValueOperator.NotEqual:
            if (!isEquals(normalizedFieldValue, condition, caseSensitiveComparison)) {
              match = true;
            }
            break;
          case ValueOperator.GreaterThan:
            if (isGreaterThan(fieldValue?.replace(',', ''), condition.replace(',', ''))) {
              match = true;
            }
            break;
          case ValueOperator.LessThan:
            if (isLessThan(fieldValue?.replace(',', ''), condition.replace(',', ''))) {
              match = true;
            }
            break;
          case ValueOperator.Contains:
            if (normalizedFieldValue?.includes(condition)) {
              match = true;
            }
            break;
          case ValueOperator.YearsOlderThan:
            if (fieldValue && condition && isDateOlderThanYears(fieldValue, condition)) {
              match = true;
            }
            break;
          case ValueOperator.YearsYoungerThan:
            if (fieldValue && condition && isDateYoungerThanYears(fieldValue, condition)) {
              match = true;
            }
            break;
          default:
            match = true; // by default show the element
            break;
        }
      });
    });
    return match;
  }

  const isEquals = (
    value1: MaybeHiddenField,
    value2: string | string[],
    isCaseSensitive = false,
  ) => {
    // Normalize null/undefined to empty string for comparison
    const normalizedValue1 = value1 ?? '';
    const normalizedValue2 = Array.isArray(value2) ? (value2[0] ?? '') : value2;

    if (Array.isArray(value1)) return value1.includes(normalizedValue2);
    if (isCaseSensitive && normalizedValue1 === normalizedValue2) return true;
    return caseInsensitiveCompare(normalizedValue1, normalizedValue2);
  };

  function caseInsensitiveCompare(val1: MaybeHiddenField, val2: MaybeHiddenField) {
    // Normalize null/undefined to empty string for comparison
    const normalizedVal1 = val1 ?? '';
    const normalizedVal2 = val2 ?? '';
    return String(normalizedVal1).toLowerCase() === String(normalizedVal2).toLowerCase();
  }

  function isGreaterThan(val1: MaybeHiddenField, val2: MaybeHiddenField) {
    // For comparison operators, treat null/undefined as empty string
    if (val1 == null || val2 == null) {
      return false;
    }

    if (valuesAreNumeric(val1, val2)) {
      return Number(val1) > Number(val2);
    }

    if (valuesAreDates(val1, val2)) {
      return Date.parse(val1) > Date.parse(val2);
    }

    return val1 > val2;
  }

  function isLessThan(val1: MaybeHiddenField, val2: MaybeHiddenField) {
    // For comparison operators, treat null/undefined as empty string
    if (val1 == null || val2 == null) {
      return false;
    }

    if (valuesAreNumeric(val1, val2)) {
      return Number(val1) < Number(val2);
    }

    if (valuesAreDates(val1, val2)) {
      return Date.parse(val1) < Date.parse(val2);
    }

    return val1 < val2;
  }

  function valuesAreNumeric(val1: MaybeHiddenField, val2: MaybeHiddenField) {
    if (val1 == null || val2 == null) return false;
    return !Number.isNaN(Number(val1)) && !Number.isNaN(Number(val2));
  }

  function valuesAreDates(val1: MaybeHiddenField, val2: MaybeHiddenField) {
    if (val1 == null || val2 == null) return false;

    if (valuesAreNumeric(val1, val2)) {
      return false;
    }

    return !Number.isNaN(Date.parse(val1)) && !Number.isNaN(Date.parse(val2));
  }

  return evaluateDisplayCondition;
}
